<template>
  <button type="button" :class="buttonClasses">
    <span class="flex min-w-0 items-center gap-2.5">
      <slot name="icon">
        <component
          v-if="icon"
          :is="icon"
          aria-hidden="true"
          :class="iconClasses"
        />
      </slot>
      <!-- Collapsed (icon rail): the label stays as the accessible name. -->
      <span v-if="label" :class="collapsed ? 'sr-only' : 'truncate'">{{
        label
      }}</span>
    </span>

    <slot v-if="!collapsed" name="right-element" />
  </button>
</template>

<script setup lang="ts">
import { type Component, computed } from "vue";

interface Props {
  label?: string;
  icon?: Component;
  active?: boolean;
  collapsed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  icon: undefined,
  active: false,
  collapsed: false,
});

// Base classes applied always
const ButtonClasses = [
  "group",
  "h-9",
  "rounded-md",
  "flex",
  "items-center",
  "gap-2",
  "text-sm",
  "font-medium",
  "text-left",
  "transition-colors",
  "duration-150",
  "focus:outline-none",
  "focus-visible:ring-2",
  "focus-visible:ring-accent/40",
  "dark:focus-visible:ring-accent-dark/40",
];

// Active item: soft tinted surface + colored text (no filled pill). Light
// mode wears the brand-book orange; dark mode keeps the purple accent,
// where the orange tint would muddy against the navy surfaces.
const buttonClasses = computed(() => [
  ...ButtonClasses,
  props.collapsed
    ? ["w-10", "justify-center", "px-0"]
    : ["w-full", "justify-between", "px-2.5"],
  props.active
    ? "bg-brand-orange/10 dark:bg-accent-soft-dark text-brand-orange dark:text-accent-dark"
    : "text-content dark:text-content-dark hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark hover:text-heading dark:hover:text-heading-dark",
]);

const iconClasses = computed(() => [
  "text-[18px]",
  "shrink-0",
  props.active
    ? "text-brand-orange dark:text-accent-dark"
    : "text-muted-text dark:text-muted-text-dark group-hover:text-heading dark:group-hover:text-heading-dark",
]);
</script>
